<template>
  <div>
    <h1 class="headline mb-4 pt-4">
      Clients

      <v-btn
        small
        class="ml-2"
        color="primary"
        @click="showClientDialog('Add')"
      >
        <v-icon small>add</v-icon>&nbsp;Add Client
      </v-btn>
    </h1>
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
      <v-btn text @click="snack = false">Close</v-btn>
    </v-snackbar>

    <!-- Client dialog -->
    <v-dialog v-model="dialogClient" max-width="600px">
      <v-card>
        <v-card-title primary-title>
          {{ dialogClientAction }} Client
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-text-field
              label="Client Name"
              v-model="dialogClientRecord.name"
              :rules="[(v) => !!v || 'Client name is required']"
              required
              outlined
              class="mb-2"
            ></v-text-field>
            <div
              v-if="
                dialogClientAction === 'Add' && similarClientNames.length > 0
              "
              class="orange--text text--darken-2 caption mb-3 ml-1"
            >
              <v-icon small color="orange darken-2">warning</v-icon>
              Warning: Similar client name(s) found:
              <strong>{{ similarClientNames.join(", ") }}</strong>
            </div>

            <!-- Contacts Section -->
            <v-card outlined class="mb-4 pa-3">
              <div class="d-flex justify-space-between align-center mb-2">
                <h3 class="subtitle-1 mb-0">Contacts</h3>
                <v-btn color="primary" small @click="addContact">
                  <v-icon small>add</v-icon>&nbsp;Add Contact
                </v-btn>
              </div>

              <div
                v-if="
                  dialogClientRecord.contacts &&
                    dialogClientRecord.contacts.length === 0
                "
                class="text-center pa-4 grey--text"
              >
                No contacts added. Click "Add Contact" to add one.
              </div>

              <v-card
                v-for="(contact, index) in dialogClientRecord.contacts"
                :key="index"
                outlined
                class="mb-2 pa-2"
              >
                <div class="d-flex justify-space-between">
                  <div class="subtitle-2">Contact #{{ index + 1 }}</div>
                  <v-btn
                    icon
                    x-small
                    color="red"
                    @click="removeContact(index)"
                    class="mt-n1 mr-n1"
                  >
                    <v-icon small>delete</v-icon>
                  </v-btn>
                </div>

                <v-row dense>
                  <v-col cols="6">
                    <v-text-field
                      label="First Name"
                      v-model="contact.firstName"
                      :rules="[(v) => !!v || 'First name is required']"
                      required
                      dense
                      outlined
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      label="Last Name"
                      v-model="contact.lastName"
                      dense
                      outlined
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-text-field
                  label="Email"
                  v-model="contact.email"
                  :rules="[
                    (v) => !v || /.+@.+\..+/.test(v) || 'Email must be valid',
                  ]"
                  dense
                  outlined
                ></v-text-field>

                <v-text-field
                  label="Phone"
                  v-model="contact.phone"
                  v-mask="'(###) ###-####'"
                  :rules="[
                    (v) => !v || v.length === 14 || 'Phone must be 10 digits',
                  ]"
                  dense
                  outlined
                ></v-text-field>
              </v-card>
            </v-card>

            <v-text-field
              label="Street Address"
              v-model="dialogClientRecord.street"
              outlined
              class="mb-2"
            ></v-text-field>

            <v-text-field
              label="Street Address 2"
              v-model="dialogClientRecord.street2"
              outlined
              class="mb-2"
            ></v-text-field>

            <v-text-field
              label="City"
              v-model="dialogClientRecord.city"
              outlined
              class="mb-2"
            ></v-text-field>

            <v-row>
              <v-col cols="6">
                <v-select
                  label="State"
                  v-model="dialogClientRecord.state"
                  :items="statesList"
                  outlined
                  class="mb-2"
                ></v-select>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  label="Postal Code"
                  v-model="dialogClientRecord.postalCode"
                  outlined
                  class="mb-2"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="dialogClient = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="saveClientDialog"
            :loading="dialogClientLoading"
            :disabled="dialogClientLoading || !valid"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Clients data table -->
    <v-card>
      <v-data-table
        :headers="headers"
        :items="clients"
        class="elevation-1"
        :search="search"
        :loading="loading"
        :custom-filter="filterByText"
        :items-per-page="15"
        :footer-props="{
          'items-per-page-options': [15, 100, -1],
        }"
        dense
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            label="Filter by client name, address, contact, or phone"
            class="mx-4 pt-4"
            outlined
            dense
          ></v-text-field>
        </template>

        <template v-slot:item.name="{ item }">
          {{ item.name }}
        </template>

        <template v-slot:item.address="{ item }">
          <p
            class="ma-0 pa-0 body-2"
            style="line-height:1.4em"
            v-if="item.street || item.city"
          >
            {{ item.street }}
            <span v-if="item.street2"> {{ item.street2 }}</span>
          </p>
          <p class="ma-0 pa-0" style="line-height:1.4em" v-if="item.city">
            {{ item.city }}<span v-if="item.state">, {{ item.state }}</span>
            {{ item.postalCode }}
          </p>
          <span v-if="!item.street && !item.city" class="grey--text">-</span>
        </template>

        <template v-slot:item.contacts="{ item }">
          <div v-if="item.contacts && item.contacts.length > 0">
            <div v-for="(contact, index) in item.contacts" :key="index">
              <div>{{ contact.firstName }} {{ contact.lastName }}</div>
              <div class="caption">
                <a :href="`mailto:${contact.email}`">{{ contact.email }}</a>
              </div>
              <div class="caption" v-if="contact.phone">
                <a :href="`tel:${contact.phone}`">{{ contact.phone }}</a>
              </div>
              <v-divider
                v-if="index < item.contacts.length - 1"
                class="my-1"
              ></v-divider>
            </div>
          </div>
          <span v-else class="grey--text">No contacts</span>
        </template>

        <template v-slot:item.edit="{ item }">
          <v-btn
            color="primary"
            text
            outlined
            small
            @click="showClientDialog('Edit', item)"
          >
            <v-icon small left>edit</v-icon> edit
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import fb from "@/main";
import { mapState } from "vuex";
import utils from "@/helpers/utils";
import { mask } from "vue-the-mask";

export default {
  directives: {
    mask,
  },
  data: () => {
    return {
      loading: false,
      clients: [],
      dialogClient: false,
      dialogClientRecord: {},
      dialogClientAction: "",
      dialogClientLoading: false,
      snack: false,
      snackText: "",
      snackColor: undefined,
      search: "",
      valid: true,
      statesList: utils.statesListShort,
      headers: [
        {
          text: "Client Name",
          align: "start",
          value: "name",
        },
        {
          text: "Address",
          align: "start",
          value: "address",
          width: "280px",
        },
        {
          text: "Contacts",
          align: "start",
          value: "contacts",
          width: "300px",
        },
        {
          text: "Edit",
          align: "center",
          sortable: false,
          value: "edit",
          width: "80px",
        },
      ],
    };
  },
  computed: {
    ...mapState(["user"]),
    similarClientNames() {
      if (
        !this.dialogClientRecord.name ||
        this.dialogClientRecord.name.length < 3
      ) {
        return [];
      }

      const searchName = this.dialogClientRecord.name.toLowerCase().trim();

      return this.clients
        .filter((client) => {
          if (
            this.dialogClientRecord.id &&
            client.id === this.dialogClientRecord.id
          ) {
            return false; // Don't match against itself when editing
          }
          const clientName = (client.name || "").toLowerCase().trim();
          return (
            clientName.includes(searchName) || searchName.includes(clientName)
          );
        })
        .map((client) => client.name)
        .slice(0, 5); // Limit to 5 similar names
    },
  },
  methods: {
    addContact() {
      if (!this.dialogClientRecord.contacts) {
        this.dialogClientRecord.contacts = [];
      }
      this.dialogClientRecord.contacts.push({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
      });
    },
    removeContact(index) {
      this.dialogClientRecord.contacts.splice(index, 1);
    },
    filterByText(value, search, item) {
      const searchTerm = search.toLowerCase();
      let contactMatch = false;

      // Check if any contacts match the search term
      if (item.contacts && item.contacts.length > 0) {
        contactMatch = item.contacts.some((contact) => {
          return (
            (contact.firstName &&
              contact.firstName.toLowerCase().indexOf(searchTerm) > -1) ||
            (contact.lastName &&
              contact.lastName.toLowerCase().indexOf(searchTerm) > -1) ||
            (contact.email &&
              contact.email.toLowerCase().indexOf(searchTerm) > -1) ||
            (contact.phone &&
              contact.phone.toLowerCase().indexOf(searchTerm) > -1)
          );
        });
      }

      return (
        (item.name && item.name.toLowerCase().indexOf(searchTerm) > -1) ||
        (item.street && item.street.toLowerCase().indexOf(searchTerm) > -1) ||
        (item.city && item.city.toLowerCase().indexOf(searchTerm) > -1) ||
        (item.state && item.state.toLowerCase().indexOf(searchTerm) > -1) ||
        contactMatch
      );
    },
    showClientDialog: function(action, record) {
      this.dialogClientAction = action;
      this.dialogClientRecord = {
        contacts: [],
        street: "",
        street2: "",
      };
      if (record) {
        this.dialogClientRecord = { ...record };

        // Ensure contacts is an array
        if (!this.dialogClientRecord.contacts) {
          this.dialogClientRecord.contacts = [];
        }

        // Ensure address fields exist
        if (!this.dialogClientRecord.street) {
          this.dialogClientRecord.street = "";
        }
        if (!this.dialogClientRecord.street2) {
          this.dialogClientRecord.street2 = "";
        }
      }
      this.dialogClient = true;
    },
    saveClientDialog: async function() {
      if (!this.$refs.form.validate()) {
        return;
      }

      const record = this.dialogClientRecord;
      const action = this.dialogClientAction;

      try {
        this.dialogClientLoading = true;

        if (action === "Add") {
          const obj = {
            name: record.name,
            street: record.street || "",
            street2: record.street2 || "",
            city: record.city || "",
            state: record.state || "",
            postalCode: record.postalCode || "",
            address: record.address || "",
            contacts: record.contacts || [],
            createdBy: this.user.email,
            createDate: new Date(),
          };

          await fb.db.collection("clients").add(obj);
        } else if (action === "Edit") {
          const obj = {
            name: record.name,
            street: record.street || "",
            street2: record.street2 || "",
            city: record.city || "",
            state: record.state || "",
            postalCode: record.postalCode || "",
            address: record.address || "",
            contacts: record.contacts || [],
            lastModifiedBy: this.user.email,
            lastModifiedDate: new Date(),
          };

          await fb.db
            .collection("clients")
            .doc(record.id)
            .update(obj);
        }

        this.snack = true;
        this.snackText = `${action} was successful for ${record.name}.`;
        this.snackColor = "success";
        this.getClients();
        this.dialogClient = false;
      } catch (e) {
        this.snack = true;
        this.snackText = `${action} had an error for ${record.name}.`;
        this.snackColor = "error";
        console.log("saveClientDialog error", e);
      }
      this.dialogClientLoading = false;
    },
    getClients: async function() {
      this.loading = true;
      try {
        const clientsRes = await fb.db
          .collection("clients")
          .orderBy("name", "asc")
          .get();

        const clients = [];
        clientsRes.forEach((r) => {
          const obj = r.data();
          obj.id = r.id;

          // Ensure contacts field exists
          if (!obj.contacts) {
            obj.contacts = [];
          }

          clients.push(obj);
        });

        this.clients = clients;
      } catch (error) {
        console.error("Error getting clients:", error);
        this.snack = true;
        this.snackText = "Error loading clients.";
        this.snackColor = "error";
      }
      this.loading = false;
    },
  },
  mounted: async function() {
    this.getClients();
  },
};
</script>
